import { TRANSACTION_DATE_COLUMN_NAME } from '@opengov/api-deprecated';
import { BasicColumnTypes, IOgqlSemanticType, SemanticTypes } from '@opengov/ogql-types';
import { COMBOS_OF_CALENDAR_UNITS, CalendarUnitCombo } from '../../../../src/utils/calendar';
import {
  IReportConfiguration,
  OrganizedByColumnType,
  ReportControlType,
  LOGICAL_TYPES,
  OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME
} from '@opengov/reporting-types';
import {
  IReportColumn,
  getColumnName,
  getDisplayName,
  isFYFPColumn,
  mapColumnToFactory
} from '@opengov/reporting-types/src/reportColumns-immer';
import { Map } from 'immutable';
import { Selector, createSelector } from 'reselect';
import { reportLocaleText } from '../../../common/locale';
import { selectCurrentReport } from '../../../common/report/selectors/reportSelectors';
import { selectHiddenControls } from '../../../common/reportConfiguration/selectors';
import { IOrganizedByDateOption, IOrganizedByOption, IOrganizedByOptions } from '../../types/reportExplorerTypes';
import { selectFiscalYearFiscalPeriodCombinedColumnFlag } from '../../../common/flags';
import { getFYFPOrganizedByLabel } from '../../../common/reportConfiguration/utils/fiscalYearFiscalPeriodUtils';

const SYSTEM_GENERATED = 'system-generated';

const sortByDisplayName = (a, b) => (b.displayName > a.displayName ? -1 : 1);

const findAndSortUnhiddenColumnsOfType = (columns: IReportColumn[], type: BasicColumnTypes): IReportColumn[] => {
  return columns && columns.filter((c) => c.type === type && c.hidden !== true).sort(sortByDisplayName);
};

const findNonSystemGeneratedColumnOfType = (columns: IReportColumn[], type): IReportColumn => {
  return (
    columns &&
    columns.find(
      (c) =>
        c.type === type &&
        (!c.semanticTypes ||
          !c.semanticTypes.find(
            (st) =>
              Object.prototype.hasOwnProperty.call(st, 'type') && (st as IOgqlSemanticType).type === SYSTEM_GENERATED
          ))
    )
  );
};

// takes a selector that returns a IReportConfiguration
// and returns selectors for the organizedBy menu options and labels.
export const reportFormSelectors = (
  source: Selector<Map<string, any>, IReportConfiguration>,
  selectHiddenColumnsFromDataMgr: Selector<Map<string, any>, string[]>
) => {
  const selectOrganizedBy = createSelector(
    source,
    (reportConfig) => reportConfig && reportConfig.visualizationQuery.organizedBy
  );

  const selectCurrentColumns = createSelector(source, (reportConfig: IReportConfiguration): IReportColumn[] => {
    const presentationConfig =
      reportConfig && reportConfig.presentationConfiguration && reportConfig.presentationConfiguration.columnNames;
    if (reportConfig && reportConfig.detailTableQuery && reportConfig.detailTableQuery.columns) {
      return reportConfig.detailTableQuery.columns.map((c) => {
        const column = mapColumnToFactory(c);
        column.displayName = getDisplayName(c, presentationConfig);
        return column;
      });
    }
  });

  // column selectors
  //////////////////////////////////////////////////////////////////////////////

  const selectSortedTextColumns = createSelector(selectCurrentColumns, (columns) =>
    findAndSortUnhiddenColumnsOfType(columns, BasicColumnTypes.Text)
  );

  const selectSortedBooleanColumns = createSelector(selectCurrentColumns, (columns) =>
    findAndSortUnhiddenColumnsOfType(columns, BasicColumnTypes.Boolean)
  );

  const selectPlainDateColumns = createSelector(selectCurrentColumns, (columns = []) => {
    const sortedColumns = findAndSortUnhiddenColumnsOfType(columns, BasicColumnTypes.Date) || [];
    return sortedColumns
      .concat(columns.filter((c) => c.semanticTypes?.includes(SemanticTypes.Calendar.GregorianYear)))
      .sort(sortByDisplayName);
  });

  const selectFiscalYearColumns = createSelector(
    selectCurrentColumns,
    (columns) =>
      columns &&
      findAndSortUnhiddenColumnsOfType(columns, BasicColumnTypes.Year).filter(
        (c) => c.name !== TRANSACTION_DATE_COLUMN_NAME
      )
  );

  const selectFiscalYearColumn = createSelector(selectCurrentColumns, (columns) =>
    findNonSystemGeneratedColumnOfType(columns, 'year')
  );

  const selectFiscalPeriodColumn = createSelector(selectCurrentColumns, (columns) =>
    findNonSystemGeneratedColumnOfType(columns, 'period')
  );

  const selectTransactionColumns = createSelector(
    selectCurrentColumns,
    (columns): IReportColumn => columns && columns.filter((c) => c.name === TRANSACTION_DATE_COLUMN_NAME)[0]
  );

  const selectFiscalYearFiscalPeriodColumn = createSelector(selectCurrentColumns, (columns) =>
    findNonSystemGeneratedColumnOfType(columns, LOGICAL_TYPES.FISCAL_YEAR_FISCAL_PERIOD)
  );

  // Intermediate options selectors
  //////////////////////////////////////////////////////////////////////////////

  const getDateUnits = (semanticTypes) => {
    if (semanticTypes?.includes(SemanticTypes.Calendar.GregorianYear)) {
      return COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.CalendarYearOnly];
    } else {
      return COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FullDate];
    }
  };

  const selectPlainDateOrganizedByOptions = createSelector(
    selectPlainDateColumns,
    (plainDateColumns): IOrganizedByDateOption[] =>
      plainDateColumns && plainDateColumns.length > 0
        ? plainDateColumns.map((dc) => {
            return {
              columnDisplayName: dc.displayName,
              columnName: dc.name,
              dateUnits: getDateUnits(dc.semanticTypes)
            };
          })
        : []
  );

  const selectFiscalYearOrganizedByOptions = createSelector(
    selectFiscalYearColumns,
    (fiscalYearColumns): IOrganizedByDateOption[] =>
      fiscalYearColumns && fiscalYearColumns.length > 0
        ? fiscalYearColumns.map((dc) => {
            return {
              columnDisplayName: dc.displayName,
              columnName: dc.name,
              dateUnits: COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FiscalYearOnly]
            };
          })
        : []
  );

  const selectTransactionDateOrganizedByOption = createSelector(
    selectCurrentReport,
    selectTransactionColumns,
    selectFiscalYearColumn,
    selectFiscalPeriodColumn,
    (report, transactionDateColumn, fiscalYear, fiscalPeriod) =>
      report &&
      report.get('viewId', null) &&
      transactionDateColumn && {
        columnName: TRANSACTION_DATE_COLUMN_NAME,
        columnDisplayName: fiscalPeriod
          ? `${fiscalPeriod.displayName} and ${fiscalYear.displayName}`
          : fiscalYear && fiscalYear.displayName,
        dateUnits: fiscalPeriod
          ? COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FiscalYearAndPeriod]
          : COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FiscalYearOnly]
      }
  );

  const selectFiscalYearFiscalPeriodOrganizedByOption = createSelector(
    selectCurrentReport,
    selectFiscalYearFiscalPeriodColumn,
    selectFiscalYearColumn,
    selectFiscalPeriodColumn,
    selectFiscalYearFiscalPeriodCombinedColumnFlag,
    (
      report,
      fiscalYearFiscalPeriodColumn: IReportColumn | undefined,
      fiscalYear: IReportColumn | undefined | null,
      fiscalPeriod: IReportColumn | undefined | null,
      canUseFYFPColumn: boolean
    ) => {
      return (
        canUseFYFPColumn &&
        report &&
        report.get('lensId', null) &&
        fiscalYearFiscalPeriodColumn &&
        fiscalYear &&
        fiscalPeriod && {
          columnName: fiscalYearFiscalPeriodColumn.name,
          columnDisplayName: `${fiscalYear.displayName} and ${fiscalPeriod.displayName}`,
          dateUnits: COMBOS_OF_CALENDAR_UNITS[CalendarUnitCombo.FiscalYearAndPeriod]
        }
      );
    }
  );

  const selectTextOrganizedByOption = createSelector(
    selectSortedTextColumns,
    selectHiddenControls,
    selectOrganizedBy,
    (textColumns, hiddenControls, organizedBy): IOrganizedByOption[] =>
      textColumns && textColumns.length > 0
        ? textColumns
            .filter((sortedTextColumn) => {
              return !(
                sortedTextColumn.name !== organizedBy.column.name &&
                hiddenControls &&
                hiddenControls.filter((hiddenControl) => {
                  return (
                    hiddenControl.columnName === sortedTextColumn.name && hiddenControl.type === ReportControlType.By
                  );
                }).length !== 0
              );
            })
            .map((dc) => {
              return {
                columnName: dc.name,
                columnDisplayName: dc.displayName
              };
            })
        : []
  );

  const selectBooleanOrganizedByOption = createSelector(
    selectSortedBooleanColumns,
    selectHiddenControls,
    selectOrganizedBy,
    (columns, hiddenControls, organizedBy): IOrganizedByOption[] =>
      columns && columns.length > 0
        ? columns
            .filter((sortedColumn) => {
              return !(
                sortedColumn.name !== organizedBy.column.name &&
                hiddenControls.filter((hiddenControl) => {
                  return hiddenControl.columnName === sortedColumn.name && hiddenControl.type === ReportControlType.By;
                }).length !== 0
              );
            })
            .map((dc) => {
              return {
                columnName: dc.name,
                columnDisplayName: dc.displayName
              };
            })
        : []
  );

  const selectAllDateOptions = createSelector(
    selectTransactionDateOrganizedByOption,
    selectFiscalYearFiscalPeriodOrganizedByOption,
    selectPlainDateOrganizedByOptions,
    selectFiscalYearOrganizedByOptions,
    (transactionDateOption, fiscalYearFiscalPeriodOption, plainDateOptions, fiscalYearColumns) => {
      const options: IOrganizedByDateOption[] = [...plainDateOptions];
      if (transactionDateOption) {
        options.push(transactionDateOption);
      }
      if (fiscalYearFiscalPeriodOption) {
        options.push(fiscalYearFiscalPeriodOption);
      }
      return options.concat(fiscalYearColumns).filter((e) => e !== null && e !== undefined);
    }
  );

  const selectOrganizedByColumn = createSelector(selectOrganizedBy, (orgBy) => orgBy && orgBy.column);

  const selectOrganizedByOptions = createSelector(
    selectAllDateOptions,
    selectTextOrganizedByOption,
    selectBooleanOrganizedByOption,
    selectHiddenColumnsFromDataMgr,
    selectOrganizedByColumn,
    (dateOptions, textOptions, booleanOptions, hiddenColsFromDataMgr, orgByCol): IOrganizedByOptions => {
      return {
        dateColumns: dateOptions.filter(
          (col) =>
            !hiddenColsFromDataMgr.includes(col.columnName) ||
            orgByCol.name === col.columnName ||
            // transaction date and fyfp columns are hidden by default
            // but users should be able to organize data by transaction date or fyfp column
            col.columnName === TRANSACTION_DATE_COLUMN_NAME ||
            col.columnName === OG_FISCAL_YEAR_FISCAL_PERIOD_COLUMN_NAME
        ),
        textColumns: textOptions
          .concat(booleanOptions)
          .filter((col) => !hiddenColsFromDataMgr.includes(col.columnName) || orgByCol.name === col.columnName)
          .sort((a, b) => a.columnDisplayName.localeCompare(b.columnDisplayName))
      };
    }
  );

  const selectLabelForOrganizedByOption = createSelector(
    selectOrganizedBy,
    selectOrganizedByOptions,
    reportLocaleText,
    selectFiscalYearFiscalPeriodCombinedColumnFlag,
    (orgBy, organizedByOptions, reportLocale, canUseFYFPColumn) => {
      const calendarUnitText = reportLocale.calendarUnits;
      if (orgBy && organizedByOptions) {
        if (canUseFYFPColumn && isFYFPColumn(orgBy.column)) {
          return getFYFPOrganizedByLabel({
            calendarUnitText,
            selection: orgBy.selection,
            dateColumns: organizedByOptions.dateColumns
          });
        } else if (orgBy.columnType === OrganizedByColumnType.Text) {
          return (
            organizedByOptions.textColumns.find((c) => {
              return c.columnName === orgBy.column.name;
            })?.columnDisplayName ||
            organizedByOptions.textColumns.find((c) => {
              return getColumnName(c.columnName) === orgBy.column.name;
            })?.columnDisplayName
          );
        } else if (orgBy?.column?.name === TRANSACTION_DATE_COLUMN_NAME) {
          const transactionDateColumn = organizedByOptions.dateColumns.find(
            (c) => c.columnName === TRANSACTION_DATE_COLUMN_NAME
          );

          if (transactionDateColumn) {
            return `${transactionDateColumn.columnDisplayName}: ${calendarUnitText[orgBy.selection]}`;
          }
        } else {
          const option =
            organizedByOptions.dateColumns.find((c) => c.columnName === orgBy.column.name) ||
            organizedByOptions.dateColumns.find((c) => getColumnName(c.columnName) === orgBy.column.name);
          if (option) {
            return `${option.columnDisplayName}: ${calendarUnitText[orgBy.selection]}`;
          }
        }
      }

      return '';
    }
  );

  const selectTargetStatusOrganizedByLabel = createSelector(
    selectOrganizedBy,
    selectOrganizedByOptions,
    reportLocaleText,
    selectFiscalYearFiscalPeriodCombinedColumnFlag,
    (orgBy, organizedByOptions, reportLocale, canUseFYFPColumn) => {
      const calendarUnitText = reportLocale.calendarUnitsSingular;
      if (orgBy && organizedByOptions) {
        if (canUseFYFPColumn && isFYFPColumn(orgBy.column)) {
          return getFYFPOrganizedByLabel({
            calendarUnitText,
            selection: orgBy.selection,
            dateColumns: organizedByOptions.dateColumns,
            showOnlyCalendarUnit: true
          });
        } else if (orgBy.columnType === OrganizedByColumnType.Text) {
          return (
            organizedByOptions.textColumns.find((c) => {
              return c.columnName === orgBy.column.name;
            })?.columnDisplayName ||
            organizedByOptions.textColumns.find((c) => {
              return c.columnName === getColumnName(orgBy.column.name);
            })?.columnDisplayName
          );
        } else if (orgBy.column.name === TRANSACTION_DATE_COLUMN_NAME) {
          const transactionDateColumn = organizedByOptions.dateColumns.find(
            (c) => c.columnName === TRANSACTION_DATE_COLUMN_NAME
          );

          if (transactionDateColumn) {
            return `${transactionDateColumn.columnDisplayName}: ${calendarUnitText[orgBy.selection]}`;
          }
        } else {
          const option =
            organizedByOptions.dateColumns.find((c) => c.columnName === orgBy.column.name) ||
            organizedByOptions.dateColumns.find((c) => c.columnName === getColumnName(orgBy.column.name));
          if (option) {
            return `${calendarUnitText[orgBy.selection]}`;
          }
        }
      }

      return '';
    }
  );

  return {
    selectTransactionDateOrganizedByOption,
    selectAllDateOptions,
    selectLabelForOrganizedByOption,
    selectOrganizedByOptions,
    selectTargetStatusOrganizedByLabel,
    selectTextOrganizedByOption,
    selectOrganizedByColumn,
    selectFiscalYearFiscalPeriodColumn,
    selectFiscalYearFiscalPeriodOrganizedByOption
  };
};
